import React, { useState } from "react";
import { Container } from "react-bootstrap";
import { motion } from "framer-motion";
import Particle from "../Particle";
import "./Experience.css";

function Experience() {
  const [expandedId, setExpandedId] = useState(null);

  const experiences = [
    {
      id: 1,
      role: "Software Engineer Intern",
      company: "GE Vernova",
      website: "https://www.gevernova.com",
      period: "Jan 2026 - Present · 1 mo",
      location: "Massy, Île-de-France, France · On-site",
      description: "Working on industrial software solutions and digital transformation initiatives at GE Vernova's power and renewable energy division.",
      achievements: [
        "Developing and implementing software solutions for power generation and renewable energy systems",
        "Contributing to digital transformation initiatives for industrial operations",
        "Working with cutting-edge technologies in the energy sector"
      ],
      technologies: ["Software Engineering", "Industrial Solutions", "Digital Transformation", "Energy Sector"]
    },
    {
      id: 2,
      role: "Full-stack Developer",
      company: "QazarTechnos cloud X p2p",
      website: "https://qazar.cloud",
      period: "Jun 2025 - Aug 2025 · 3 mos",
      location: "Montpellier, Occitanie, France · On-site",
      description: "Developing innovative mobile and web applications using React Native, TypeScript, and Go microservices for cloud & p2p platforms.",
      achievements: [
        "Built cross-platform mobile applications using React Native CLI (without Expo) for iOS and Android",
        "Developed responsive web applications using Gusstack UI framework based on Tailwind CSS",
        "Implemented microservices architecture using Go for scalable backend systems",
        "Integrated video functionality including 1:1 exchanges, conferences, and webinars",
        "Collaborated on AI audio agent integration via API for enhanced user interactions",
        "Worked with PostgreSQL databases and caching systems for optimal performance"
      ],
      technologies: ["React Native", "TypeScript", "Go", "Gusstack", "Tailwind CSS", "PostgreSQL", "Microservices", "Video Integration", "AI Audio Agents", "Mobile Development"]
    },
    {
      id: 3,
      role: "Mobile Developer",
      company: "FairFleet",
      website: "https://fly.fairfleet.com/",
      period: "Aug 2024 - Sep 2024 · 2 mos",
      location: "Munich, Bavaria, Germany · Remote",
      description: "Developed mobile applications using Flutter and FlutterFlow to create efficient and responsive user interfaces.",
      achievements: [
        "Designed and built mobile app interfaces using FlutterFlow to accelerate the prototyping phase",
        "Transformed FlutterFlow prototypes into clean, maintainable Dart code for full customization and performance",
        "Improved user experience through iterative UI/UX design and user feedback integration"
      ],
      technologies: ["Flutter", "Dart", "FlutterFlow", "User Experience (UX)", "Mobile Application Development", "User Interface Design"]
    },
    {
      id: 4,
      role: "Developer Analyst",
      company: "Deepway",
      website: "https://deepway.fr/",
      period: "Jun 2024 - Aug 2024 · 3 mos",
      location: "La Ciotat, Provence-Alpes-Côte d'Azur, France · On-site",
      description: "Worked on embedded systems optimizations and graphical interface improvements.",
      achievements: [
        "Led the migration of a freediving computer from ATMEGA2560 to STM32/ESP32, reducing execution time by 30% and power consumption by 25%",
        "Configured and benchmarked graphical libraries, improving UI responsiveness by 40%",
        "Gained hands-on experience in embedded systems and technical project management"
      ],
      technologies: ["Embedded Systems", "STM32", "ESP32", "UI Development", "Technical Project Management"]
    },
    {
      id: 5,
      role: "Intern",
      company: "Ecole Supérieure Privée d'Ingénierie et de Technologies - ESPRIT",
      website: "https://esprit.tn/",
      period: "Jul 2023 - Aug 2023 · 2 mos",
      location: "Tunis, Tunisia · On-site",
      description: "Improved testing infrastructure and automated migration processes.",
      achievements: [
        "Migrated 150+ non-regression tests to Jest, reducing testing time by 70%",
        "Developed an automated tool to streamline the migration process and ensure test consistency",
        "Improved test coverage from 65% to 92% and identified 27 potential bugs during validation"
      ],
      technologies: ["JavaScript", "Jest", "Project Teams", "Script Shell", "Test Automation"]
    },
    {
      id: 6,
      role: "Intern",
      company: "Assurances BIAT",
      website: "https://www.assurancesbiat.com.tn/",
      period: "Jun 2023 - Jul 2023 · 2 mos",
      location: "Tunis, Tunisia · On-site",
      description: "Focused on data visualization and business intelligence solutions.",
      achievements: [
        "Gained strong skills in data manipulation and building visual dashboards using Power BI",
        "Contributed to the design and development of interactive dashboards, enhancing performance and trend analysis",
        "Strengthened teamwork, problem-solving, and data-driven communication through real project experience"
      ],
      technologies: ["Microsoft Power BI", "Data Visualization", "Business Intelligence", "KPI Dashboards"]
    },
    {
      id: 7,
      role: "Intern",
      company: "Tunisie Telecom",
      website: "https://www.tunisietelecom.tn/particulier/",
      period: "Aug 2022 - Sep 2022 · 2 mos",
      location: "Tunisia · On-site",
      description: "Worked with telecommunications tools and equipment in real-world scenarios.",
      achievements: [
        "Gained hands-on experience with telecommunications tools and equipment in real-world scenarios",
        "Demonstrated strong teamwork and technical communication skills in collaborative environments",
        "Built solid knowledge of telecom network technologies and protocols"
      ],
      technologies: ["Telecommunications", "Network Technologies", "Technical Communication", "Teamwork"]
    },
    {
      id: 8,
      role: "Protocol Chief",
      company: "JCI TUNISIE",
      website: "https://jci.cc/",
      period: "Jan 2019 - Jan 2022 · 3 yrs 1 mo",
      location: "Tunis, Tunis, Tunisia · On-site",
      description: "Managed official ceremonies and coordinated with stakeholders for high-profile events.",
      achievements: [
        "Oversaw event protocol and ensured compliance with international standards during official ceremonies and public engagements",
        "Coordinated with internal teams and external stakeholders to manage high-profile delegations and guests",
        "Contributed to the promotion of JCI's mission of empowering young active citizens to create positive change",
        "Developed organizational and communication skills through planning and execution of regional and national events"
      ],
      technologies: ["Event Management", "Protocol", "Leadership", "Communication", "Stakeholder Management"]
    }
  ];

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <Container fluid className="experience-section">
      <Particle />
      <Container>
        <h1 className="project-heading">
          My Professional <strong className="purple">Experience </strong>
        </h1>
        <p className="experience-description">
          My work history and the impact I've made in previous roles
        </p>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="experience-timeline"
        >
          {experiences.map((exp) => (
            <motion.div
              key={exp.id}
              variants={itemVariants}
              className={`experience-card ${expandedId === exp.id ? 'expanded' : ''}`}
              onClick={() => toggleExpand(exp.id)}
            >
              <div className="experience-card-header">
                <div className="experience-role-container">
                  <h3 className="experience-role">{exp.role}</h3>
                  <h4 className="experience-company">
                    {exp.company}
                    {exp.website && (
                      <a 
                        href={exp.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="company-link"
                        onClick={(e) => e.stopPropagation()}
                        title={`Visit ${exp.company} website`}
                      >
                        <i className="fas fa-external-link-alt"></i>
                      </a>
                    )}
                  </h4>
                </div>
                <div className="experience-period-container">
                  <p className="experience-period">{exp.period}</p>
                  <p className="experience-location">{exp.location}</p>
                </div>
              </div>

              <p className="experience-description">{exp.description}</p>

              {expandedId === exp.id && (
                <div className="experience-details">
                  <h5 className="achievements-title">Key Achievements</h5>
                  <ul className="achievements-list">
                    {exp.achievements.map((achievement, index) => (
                      <motion.li 
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        {achievement}
                      </motion.li>
                    ))}
                  </ul>

                  <h5 className="tech-title">Technologies Used</h5>
                  <div className="tech-tags">
                    {exp.technologies.map((tech, index) => (
                      <motion.span 
                        key={index} 
                        className="tech-tag"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                  
                  {/* Add a visible link button at the bottom of expanded view */}
                  {exp.website && (
                    <div className="website-link-container">
                      <a 
                        href={exp.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="website-link-button"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="fas fa-globe"></i> Visit Company Website
                      </a>
                    </div>
                  )}
                </div>
              )}

              <div className="expand-indicator">
                {expandedId === exp.id ? "Click to collapse" : "Click to expand"}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Container>
  );
}

export default Experience;